// Play / Planet tooltip (docs/02-Game-Design/Alignments.md): a tap on a
// planet opens a small panel at it, above the planet when there is room.
// Never surrounded: how to reveal its alignments. In force: its name with
// what the alignment adds, the alignment, the rule. Free again: "inactive",
// the alignment, the rule, how to reactivate it. Any tap elsewhere closes it.
import { alignmentName, planetAppearance, planetName, planetsOf, scorePlay, type PlayLevel, type PlayState } from '@wits/core';
import { useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { PlanetIcon } from '../sky/PlanetIcon';

export interface PlanetTipAt {
  planet: number;
  /** The planet's centre on the screen, px. */
  x: number;
  y: number;
}

interface Props {
  level: PlayLevel;
  board: PlayState;
  tip: PlanetTipAt;
  onClose: () => void;
}

const WIDTH = 250;
const MARGIN = 10;
/** The tip sits below the planet when the planet is this close to the top. */
const BELOW_WHEN_ABOVE = 230;
/** From the planet's centre to the panel's edge. */
const GAP = 22;

export function PlanetTip({ level, board, tip, onClose }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const score = useMemo(() => scorePlay(level, board), [level, board]);
  const planet = planetsOf(level).find((p) => p.id === tip.planet);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => ref.current && !ref.current.contains(e.target as Node) && onClose();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  if (!planet) return null;
  const name = planetName(planetAppearance(level, planet).kind);
  const status = score.alignments.find((a) => a.planet.id === planet.id) ?? null;
  const state = !status ? 'unrevealed' : status.active ? 'active' : 'inactive';
  const below = tip.y < BELOW_WHEN_ABOVE;
  const width = typeof window === 'undefined' ? 1024 : window.innerWidth;
  const left = Math.min(Math.max(MARGIN, tip.x - 36), width - WIDTH - MARGIN);
  const style: CSSProperties & Record<'--caret-x', string> = { left, width: WIDTH, '--caret-x': `${tip.x - left}px` };
  if (below) style.top = tip.y + GAP;
  else style.bottom = (typeof window === 'undefined' ? 768 : window.innerHeight) - tip.y + GAP;
  return (
    <div ref={ref} className={below ? 'panel planet-tip is-below' : 'panel planet-tip'} style={style} role="dialog" aria-label={`${name}`}>
      <div className="planet-tip-head">
        <PlanetIcon level={level} planet={planet} size={22} state={state} />
        <span className="planet-tip-name">{name}</span>
        {status && state === 'active' && <span className="planet-tip-points">+ {status.points}</span>}
        {status && state === 'inactive' && <span className="planet-tip-points is-inactive">inactive</span>}
      </div>
      {!status && <div className="planet-tip-rule">Create a constellation that surrounds {name} to reveal its three alignments.</div>}
      {status && (
        <>
          <div className="planet-tip-alignment">{alignmentName(status.alignment)}</div>
          <div className="planet-tip-rule">{status.text}</div>
          {state === 'inactive' && <div className="planet-tip-how">Create a constellation that surrounds {name} to reactivate its alignment.</div>}
        </>
      )}
    </div>
  );
}
